import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import Button from "../../../../../../components/button/Button";
import EditDots from "../../../../../../components/EditDots/EditDots";
import ConfirmModal from "../../../../../../components/modal/ConfirmModal";
import { SettingsConfirmModal } from "../../../../../../components/modal/SettingsConfirmModal";
import ReassignedSvg from "../../../../../../components/svg/ReassignedSvg";
import { getInitials } from "../../../../../../constants/constants";
import {
  WorkDetailsModel,
  WorkJobAssignee,
} from "../../../../../../factories/work/model/work-details.model";
import useOnClickOutside from "../../../../../../hooks/useOnClickOutside";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetUsersQuery } from "../../../../../../slices/UserSlice";
import {
  useArchiveOrUnarchiveMutation,
  useDeleteWorkMutation,
  useUpdateWorkAssigneesMutation,
} from "../../../../../../slices/WorkSlice";
import { useNavigate } from "react-router-dom";
import { BsArchive } from "react-icons/bs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FaBoxArchive } from "react-icons/fa6";

type Props = {
  work: WorkDetailsModel;
};

const MAX_ASSIGNEES = 5;

const getWorkAssignees = (work: WorkDetailsModel): WorkJobAssignee[] => {
  if (work.jobAssignees?.length) {
    return work.jobAssignees.filter((assignee) => assignee.assignedUser);
  }

  if (!work.assignedUser) {
    return [];
  }

  return [
    {
      jobId: work.id,
      assignedUserId: work.assignedUserId,
      assignedUser: work.assignedUser,
      receiveEmailNotification: true,
      id: work.assignedUserId,
      relayId: null,
      createdAt: work.createdAt,
      createdTimeUTC: null,
      editedTimeUTC: null,
    },
  ];
};

const areStringArraysEqual = (first: string[], second: string[]): boolean => {
  if (first.length !== second.length) {
    return false;
  }

  const firstSorted = [...first].sort();
  const secondSorted = [...second].sort();

  return firstSorted.every((item, index) => item === secondSorted[index]);
};

export const WorkButtons: FC<Props> = ({ work }) => {
  const [isOpenReassign, setIsOpenReassign] = useState(false);
  const { data: users } = useGetUsersQuery(getLinkedFirm()?.orgId);
  const user = useAppSelector((state) => state.appReducer.user);

  const [deleteWork, { isSuccess }] = useDeleteWorkMutation();
  const [updateWorkAssignees, { isLoading: isUpdatingAssignees }] =
    useUpdateWorkAssigneesMutation();
  const [activateDeactivate] = useArchiveOrUnarchiveMutation();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isArchived, setIsArchived] = useState(work.isArchived);
  const [isOpenActivation, setIsOpenActivation] = useState(false);
  const [draftAssigneeIds, setDraftAssigneeIds] = useState<string[]>(() =>
    getWorkAssignees(work).map((assignee) => assignee.assignedUserId),
  );

  const usersRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const workAssigneeIds = getWorkAssignees(work).map(
    (assignee) => assignee.assignedUserId,
  );
  const isDraftAssigneesChanged = !areStringArraysEqual(
    draftAssigneeIds,
    workAssigneeIds,
  );

  const closeReassignMenu = () => {
    setDraftAssigneeIds(workAssigneeIds);
    setIsOpenReassign(false);
  };

  useOnClickOutside(usersRef, closeReassignMenu);

  useEffect(() => {
    setIsArchived(work.isArchived);
  }, [work]);

  useEffect(() => {
    if (!isOpenReassign) {
      setDraftAssigneeIds(workAssigneeIds);
    }
  }, [isOpenReassign, work.id, work.assignedUserId, work.jobAssignees]);

  useEffect(() => {
    if (isSuccess) {
      setConfirmDelete(false);
      navigate(-1);
    } else {
      setConfirmDelete(false);
    }
  }, [isSuccess]);

  const toggleReassignMenu = () => {
    if (!isOpenReassign) {
      setDraftAssigneeIds(workAssigneeIds);
    }

    setIsOpenReassign(!isOpenReassign);
  };

  const onClickReassignMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const onToggleDraftAssignee = (assigneeId: string) => {
    setDraftAssigneeIds((currentIds) => {
      if (currentIds.includes(assigneeId)) {
        return currentIds.filter((item) => item !== assigneeId);
      }

      if (currentIds.length >= MAX_ASSIGNEES) {
        return currentIds;
      }

      return [...currentIds, assigneeId];
    });
  };

  const onSaveAssignees = async () => {
    if (
      draftAssigneeIds.length === 0 ||
      draftAssigneeIds.length > MAX_ASSIGNEES ||
      !isDraftAssigneesChanged ||
      isUpdatingAssignees
    ) {
      return;
    }

    try {
      await updateWorkAssignees({
        jobId: work.id,
        assignedByUserId: work.assignedByUserId || user.id,
        userIds: draftAssigneeIds,
      }).unwrap();

      toast.success("Work assignees updated successfully");
      setIsOpenReassign(false);
    } catch {
      toast.error("Failed to update work assignees");
    }
  };

  return (
    <div className={"flex gap-2"}>
      <div
        className={
          "relative flex w-fit items-center justify-center rounded-[8px] font-normal transition-all duration-200 hover:border-gray-200 hover:bg-gray-200"
        }>
        <EditDots>
          <div>
            <div ref={usersRef} className={"w-[150px]"}>
              <div
                className={
                  "flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1 text-center text-base font-normal hover:bg-gray-300"
                }
                onClick={toggleReassignMenu}>
                <div className={"text-purple"}>
                  <ReassignedSvg size={"18px"} />
                </div>
                Reassign
              </div>

              {isOpenReassign && (
                <div
                  onClick={onClickReassignMenu}
                  className={
                    "absolute right-0 top-8 z-[3] flex max-h-[300px] w-[245px] flex-col space-y-[2px] overflow-hidden rounded-[10px] border-[1px] border-gray-300 bg-white text-[13px] shadow-lg"
                  }>
                  <div
                    className={
                      "link-primary border-b-[1px] border-b-primary px-4 py-2 text-[14px]"
                    }>
                    Reassign work to:
                  </div>
                  <div className={"px-4 pt-2 text-xs text-[#8B98A8]"}>
                    {draftAssigneeIds.length} / {MAX_ASSIGNEES} selected
                  </div>
                  <div className={"overflow-y-auto"}>
                    {users?.map((item) => {
                      const isSelected = draftAssigneeIds.includes(item.id);
                      const isDisabled =
                        !isSelected && draftAssigneeIds.length >= MAX_ASSIGNEES;

                      return (
                        <label
                          className={
                            "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:disabled]:hover:bg-white has-[:disabled]:hover:text-inherit mt-2 flex cursor-pointer items-center space-x-[4px] px-4 last-of-type:mb-2 hover:bg-blue-400 hover:text-white"
                          }
                          key={item?.id}>
                          <input
                            type={"checkbox"}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => onToggleDraftAssignee(item.id)}
                            className={
                              "checkbox mr-1 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
                            }
                          />
                          <AvatarPlaceholder
                            size={"2xs"}
                            type={"custom"}
                            avatarCustomColor={item.userProfile.color}
                            label={getInitials(
                              item?.userProfile?.firstName || "",
                              item?.userProfile?.lastName || "",
                            )}
                          />
                          <div>{`${item.userProfile?.firstName} ${item.userProfile?.lastName}`}</div>
                        </label>
                      );
                    })}
                  </div>
                  <div
                    className={
                      "grid grid-cols-2 gap-2 border-t border-gray-200 p-2"
                    }>
                    <Button
                      fullWidth
                      label={"Cancel"}
                      size={"custom"}
                      colorType={"outline"}
                      extraClasses={"btn-xs"}
                      buttonType={"button"}
                      onClick={closeReassignMenu}
                    />
                    <Button
                      fullWidth
                      label={"Save"}
                      size={"custom"}
                      extraClasses={"btn-xs"}
                      buttonType={"button"}
                      onClick={onSaveAssignees}
                      isLoading={isUpdatingAssignees}
                      disabled={
                        draftAssigneeIds.length === 0 ||
                        draftAssigneeIds.length > MAX_ASSIGNEES ||
                        !isDraftAssigneesChanged ||
                        isUpdatingAssignees
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              className={
                "flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1 text-base font-normal hover:bg-gray-300"
              }
              onClick={() => {
                setIsOpenActivation(true);
              }}>
              <BsArchive size={18} className={"text-purple"} />
              {isArchived ? "Move to active" : "Archive"}
            </div>
            {isArchived && (
              <div
                onClick={() => {
                  setConfirmDelete(true);
                }}
                className={
                  "flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1 text-base font-normal hover:bg-gray-300"
                }>
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className={"text-purple w-[18px]"}
                />
                Delete
              </div>
            )}
          </div>
        </EditDots>
      </div>
      {confirmDelete && (
        <ConfirmModal
          confirm={() => {
            deleteWork({ body: [work.id] });
          }}
          close={() => setConfirmDelete(false)}
          title={"Delete work?"}
          message={
            <>
              <div>You are about to delete this work item. All progress,</div>
              <div>
                {" "}
                including <span className={"font-bold"}>Files</span> and{" "}
                <span className={"font-bold"}>Notes</span>, will be deleted.
              </div>
              <div> Do you wish to proceed?</div>
            </>
          }
        />
      )}
      {isOpenActivation && (
        <SettingsConfirmModal
          onClose={() => setIsOpenActivation(false)}
          onConfirm={() => {
            !isArchived
              ? activateDeactivate({
                  body: [work.id] || [],
                  archiveWork: true,
                })
              : activateDeactivate({
                  body: [work.id] || [],
                  archiveWork: false,
                });
            setIsOpenActivation(false);
            setIsArchived(!work.isArchived);
          }}
          icon={<FaBoxArchive size={"18px"} />}
          title={
            !isArchived
              ? `Archive "${work.name}" work?`
              : `Reactivate "${work.name}" work?`
          }
          description={
            !isArchived
              ? `Are you sure you want to archive this work? It will be  moved to the "Archived" section. You can reactivate it at any time.`
              : `You are about to reactivate this work. It will be
            removed from the "Archived" section. You can always archive them later.`
          }
          confirmText={"Continue"}
          variant={"primary"}
        />
      )}
    </div>
  );
};
